import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { bubEvents } from 'shared/events/bub_events';
import { userData } from 'javascripts/shared/utils/user_data';
import { useQueryParam } from 'javascripts/hooks/use-query-param';
import { useToggle } from 'javascripts/hooks/use-toggle';

import { PillArray } from 'javascripts/components/pill_array';

import styles from 'stylesheets/components/audiobook/audiobook_description_section';

export function AudiobookDescriptionSection({ audiobook }) {
  // Use the blurb as the main description with the truncated publisher description below if the blurb exists
  // Otherwise just use the truncated publisher description as the main description
  const [fullDescriptionExpanded] = useQueryParam('full_desc', false);
  const [showFullDescription, toggleShowFullDescription] = useToggle(fullDescriptionExpanded);

  function renderCategoryPills() {
    if (_.isEmpty(audiobook.tags) || !userData.isLoggedIn()) {
      return null;
    }

    return (
      <div className={styles.categoryPills} data-testid='audiobook-description-section-category-pills'>
        <PillArray labels={audiobook.tags} trackingCategory='Book pills' trackingLabel='Tag' />
      </div>
    );
  }

  function renderDescription(htmlText, renderHiddenTextForSeo = false) {
    if (!showFullDescription && audiobook.bookDescriptionShort && renderHiddenTextForSeo) {
      let toRemove = 0;

      if (audiobook.bookDescriptionShort.endsWith('...</p>')) {
        toRemove = 7;
      } else if (audiobook.bookDescriptionShort.endsWith('</p>')) {
        toRemove = 4;
      } else if (audiobook.bookDescriptionShort.endsWith('...')) {
        toRemove = 3;
      }

      const truncatedTextLength = audiobook.bookDescriptionShort.length - toRemove;

      const additionalText = audiobook.bookDescription.substr(truncatedTextLength);
      htmlText += `<span class="${styles.hiddenText}" aria-hidden="true" data-testid="audiobook-description-section-hidden-text">${additionalText}</span>`;
    }

    return (
      <div
        className={showFullDescription ? styles.descriptionText : styles.descriptionTextShortened }
        data-testid='audiobook-description-section-description'
        dangerouslySetInnerHTML={{ __html: htmlText }}
      ></div>
    );
  }

  function onClickShowMore() {
    bubEvents.track('Publisher description', 'Clicked link', {
      label: showFullDescription ? 'Less' : 'More',
    });
    toggleShowFullDescription();
  }

  function renderPrimaryDescription() {
    if (!_.isEmpty(audiobook.editorialDescription)) {
      return renderDescription(audiobook.editorialDescription);
    }

    if (_.isEmpty(audiobook.bookDescriptionShort)) {
      return renderDescription(audiobook.bookDescription);
    }

    return (
      <div className={styles.showMoreDescription}>
        {renderDescription(showFullDescription ? audiobook.bookDescription : audiobook.bookDescriptionShort, true)}
        <button className={styles.showMoreLink} onClick={onClickShowMore}>
          {showFullDescription ? 'less' : 'more'}
        </button>
      </div>
    );
  }

  function renderSecondaryDescription() {
    if (_.isEmpty(audiobook.editorialDescription)) {
      return null;
    }

    if (_.isEmpty(audiobook.bookDescriptionShort)) {
      return (
        <div className={styles.secondaryDescription}>
          <h2 className={styles.secondaryDescriptionTitle}>Publisher Description</h2>
          <div className={styles.secondaryDescriptionText}>{renderDescription(audiobook.bookDescription)}</div>
        </div>
      );
    }

    return (
      <div className={styles.secondaryDescription}>
        <h2 className={styles.secondaryDescriptionTitle}>Publisher Description</h2>
        <div className={classNames(styles.showMoreDescription, styles.secondaryDescriptionText)}>
          {renderDescription(showFullDescription ? audiobook.bookDescription : audiobook.bookDescriptionShort, true)}
          <button className={styles.showMoreLink} onClick={onClickShowMore}>
            {showFullDescription ? 'less' : 'more'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.descriptionSection}>
      {renderPrimaryDescription()}
      {renderCategoryPills()}
      {renderSecondaryDescription()}
    </div>
  );
}

AudiobookDescriptionSection.propTypes = {
  audiobook: PropTypes.shape({
    abridged: PropTypes.bool.isRequired,
    bookDescription: PropTypes.string.isRequired,
    bookDescriptionShort: PropTypes.string,
    editorialDescription: PropTypes.string,
    featuredLists: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
    limitedTimeOffer: PropTypes.bool.isRequired,
    publisher: PropTypes.string.isRequired,
    releaseDate: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        displayName: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
};
